import { Attributes, DateUtils, LookupBase, NumberUtils } from '@singularsystems/neo-core';
import Decimal from 'decimal.js-light';
import Dividend from '../Dividend';
import DividendLookup from './DividendLookup';

export default class DividendAccrualLookup extends LookupBase {

    constructor() {
        super();
        this.makeBindable();
    }

    public dividendAccrualId: number = 0;

    @Attributes.Nullable()
    public dividendCycleDateId: number | null = null;

    @Attributes.Nullable()
    public dividendId: number | null = null;

    @Attributes.ChildObject(Dividend)
    public dividend: Dividend | null = null;

    @Attributes.Date()
    public startDate: Date = new Date();

    @Attributes.Date()
    public endDate: Date = new Date();

    @Attributes.Integer()
    public noOfDays: number = 0;

    @Attributes.Float()
    public linkedRate: number = 0;

    @Attributes.Float()
    public investmentRate: number = 0;

    @Attributes.Float()
    public compoundedBalance: number = 0;

    @Attributes.Float()
    public openingAccrued: number = 0;

    @Attributes.Float()
    public accruedForPeriod: number = 0;

    @Attributes.Float()
    public closingAccrued: number = 0;

    public isIgnored: boolean = false;

    public hasLetter: boolean = false;

    public isExported: boolean = false;

    // Client only properties / methods

    public dividendLookup: DividendLookup | null = null;

    public get isDividend() {
        if (this.dividend !== null) {
            return !this.dividend.outOfCycle(this.startDate);
        } else {
            return this.dividendCycleDateId !== null;
        }
    }

    public get isDividendIgnored() {
        return this.dividend !== null && this.dividend.shouldIgnore(this.startDate);
    }

    public get dividendNotes() {
        if (this.dividend) {
            if (this.dividend.notes) {
                return this.dividend.notes;
            } else if (this.dividend.outOfCycle(this.startDate)) {
                return `Moved to ${DateUtils.format(this.dividend.dividendDate!, "dd MMM yyyy")}`;
            }
        }
        return null;
    }

    @Attributes.Float()
    public get dividendAmount() {
        if (this.isDividend) {
            return this.dividend?.dividendAmount ?? this.openingAccrued;
        } else {
            return null;
        }
    }

    public get dividendCycleDate() {
        if (this.dividendCycleDateId) {
            return this.startDate;
        }
        return null;
    }

    public getAccrualAtDate(date: Date, reduceByStartDividend: boolean) {

        const opening = reduceByStartDividend ? new Decimal(this.closingAccrued).minus(this.accruedForPeriod) : new Decimal(this.openingAccrued);

        const days = DateUtils.difference(this.startDate, date).totalDays;
        let accrued = new Decimal(this.accruedForPeriod);
        return NumberUtils.round(accrued.dividedBy(this.noOfDays).times(days).add(opening), 2).toNumber();
    }
}